import type { ReactElement } from 'react';
import { ReportCard } from './components/ReportCard.js';

interface ReportDef {
  to: string;
  title: string;
  description: string;
  status: 'ready' | 'soon';
  source: string;
}

/**
 * Hub de la sección Reports. La página /costs sigue como vista rápida de un
 * eje; acá viven los reportes analíticos serios. Las tarjetas 'soon' marcan
 * las fases siguientes del plan (ver docs/architecture/09-client-and-admin.md).
 */
const REPORTS: ReportDef[] = [
  {
    to: '/reports/pivot',
    title: 'Pivot · consumo',
    description: 'Cruzá dos dimensiones (provider × kind, módulo × modelo…) con filtros combinables y drill-down.',
    status: 'ready',
    source: 'spans · gasto real',
  },
  {
    to: '/reports/cost-detail',
    title: 'Detalle de gasto',
    description: 'En qué se fue la plata, por operación (reply, distill, recall, TTS…). Filtrable por modelo.',
    status: 'ready',
    source: 'spans · por operación',
  },
  {
    to: '/reports/transactions',
    title: 'Transacciones',
    description: 'El nivel más profundo: cada llamada individual, cronológica (más reciente primero). Linkea a la sesión.',
    status: 'ready',
    source: 'spans · tx-a-tx',
  },
  {
    to: '/reports/pnl',
    title: 'P&L · margen',
    description: 'Revenue vs costo del proveedor → margen y % por día/modelo/provider/usuario.',
    status: 'ready',
    source: 'spans · revenue vs cost',
  },
  {
    to: '/reports/trend',
    title: 'Tendencia · run-rate',
    description: 'Período contra período (MoM/WoW) y proyección de fin de mes.',
    status: 'soon',
    source: 'spans',
  },
  {
    to: '/reports/model-efficiency',
    title: 'Eficiencia por modelo',
    description: 'Costo/llamada, tokens, cache hit, latencia p50/p95 y retry rate por modelo, para routing.',
    status: 'ready',
    source: 'llm_calls',
  },
  {
    to: '/reports/voice-anatomy',
    title: 'Anatomía de voz',
    description: 'Breakdown LLM/TTS/STT/PA/Vapi y cache hit ratio del consumo de tokens.',
    status: 'soon',
    source: '*_calls',
  },
  {
    to: '/reports/by-module',
    title: 'Por módulo',
    description: 'Costo por módulo vertical, costo/sesión y usuarios activos.',
    status: 'ready',
    source: 'spans · voice_sessions',
  },
  {
    to: '/reports/unit-economics',
    title: 'Unit economics',
    description: 'Costo por sesión/turno/usuario, ARPU y distribución de spenders.',
    status: 'soon',
    source: 'credit_transactions',
  },
];

export function ReportsHubPage(): ReactElement {
  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="page-sub">
            Análisis de consumo y costos. Gasto real (provider cost) y margen en reportes
            separados; sintéticos del simulator excluidos por default.
          </p>
        </div>
      </div>
      <div className="pad-7">
        <div className="report-grid">
          {REPORTS.map((r) => (
            <ReportCard
              key={r.to}
              to={r.to}
              title={r.title}
              description={r.description}
              status={r.status}
              source={r.source}
            />
          ))}
        </div>
      </div>
    </>
  );
}
